/* Same-origin Shopify uploads with real transfer progress; never retries a POST. */
(() => {
  document.querySelectorAll('[data-simple-saving-dialog], [data-relief-saving-dialog]').forEach(dialog => {
    dialog.style.width = 'min(360px, calc(100% - 32px))';
    if (dialog.firstElementChild) Object.assign(dialog.firstElementChild.style, { minWidth: '0', width: '100%', boxSizing: 'border-box' });
  });
  // A returned history entry must never continue an earlier purchase task.
  let pageVersion = 0;
  let cartVersion = 0, cartSection = null;
  const invalidateCartSection = () => { cartSection = null; return ++cartVersion; };
  const takeCartSection = () => { const section = cartSection; cartSection = null; return section; };
  const isCartSectionCurrent = section => section.version === cartVersion;
  window.addEventListener('pagehide', () => { pageVersion += 1; invalidateCartSection(); });
  const beginAttempt = () => {
    const version = pageVersion;
    const isActive = () => version === pageVersion;
    const check = () => {
      if (isActive()) return;
      const error = new Error('Purchase interrupted by navigation.');
      error.code = 'PAGE_LEFT';
      throw error;
    };
    return { isActive, check, wait: async promise => { const value = await promise; check(); return value; } };
  };
  const send = (url, body, { message, title, titleText = 'ADDING YOUR PERSONALIZED GIFT TO CART', savingText = 'Saving to cart' } = {}) => new Promise((resolve, reject) => {
    const target = new URL(url, location.href);
    if (target.origin !== location.origin) { reject(new Error('The cart address could not be verified. Refresh and try again.')); return; }
    const cartMutation = /\/cart\/(add|change|update|clear)\.js$/.test(target.pathname);
    const version = cartMutation ? invalidateCartSection() : null;
    const bundle = /\/cart\/add\.js$/.test(target.pathname) && body instanceof FormData;
    if (bundle) {
      body.set('sections', 'main-cart-simple');
      body.set('sections_url', target.pathname.replace(/\/add\.js$/, ''));
    }
    const bytes = [...body.values()].reduce((sum, value) => sum + (value instanceof Blob ? value.size : String(value).length), 0);
    const hasFiles = [...body.values()].some(value => value instanceof Blob);
    const xhr = new XMLHttpRequest();
    let phase = hasFiles ? 'uploading' : 'saving', percent = null, settled = false;
    let progress = message?.parentElement.querySelector('[data-cart-upload-progress]');
    if (message && !progress) {
      progress = document.createElement('progress');
      progress.dataset.cartUploadProgress = '';
      progress.max = 100;
      progress.setAttribute('aria-label', 'Customization upload progress');
      progress.style.cssText = 'width:100%;height:8px;accent-color:#2c7c66';
      message.after(progress);
    }
    const render = (slow = false) => {
      if (title) title.textContent = titleText;
      if (message) {
        message.setAttribute('role', 'status');
        message.setAttribute('aria-live', 'polite');
        message.textContent = phase === 'uploading'
          ? `Uploading…${percent === null ? '' : ` ${percent}%`}`
          : `${savingText}…`;
      }
      if (progress) {
        progress.hidden = false;
        if (percent === null) progress.removeAttribute('value'); else progress.value = percent;
      }
    };
    const slowTimer = setInterval(() => render(true), 10000);
    const finish = (error, response) => {
      if (settled) return;
      settled = true;
      clearInterval(slowTimer);
      window.removeEventListener('pagehide', cancelUpload);
      if (progress) progress.hidden = true;
      if (cartMutation && (error || version !== cartVersion)) invalidateCartSection();
      if (error) reject(error); else resolve(response);
    };
    const cancelUpload = () => xhr.abort();
    window.addEventListener('pagehide', cancelUpload, { once: true });
    xhr.upload.addEventListener('progress', event => {
      if (settled || phase !== 'uploading' || !event.lengthComputable) return;
      const next = Math.min(100, Math.floor(event.loaded / event.total * 100));
      if (next !== percent) { percent = next; render(); }
    });
    xhr.upload.addEventListener('load', () => { if (!settled) { phase = 'saving'; render(); } });
    xhr.addEventListener('load', () => {
      const status = xhr.status, text = xhr.responseText;
      if (bundle && status >= 200 && status < 300 && version === cartVersion) {
        try {
          const html = JSON.parse(text).sections?.['main-cart-simple'];
          if (typeof html === 'string') cartSection = { html, version };
        } catch { /* The caller retains receipt parsing and verification. */ }
      }
      finish(null, { ok: status >= 200 && status < 300, status, json: async () => JSON.parse(text) });
    });
    xhr.addEventListener('error', () => finish(new Error('The connection was interrupted. Checking whether your customization was saved…')));
    xhr.addEventListener('abort', () => finish(new Error('The upload was interrupted. Checking your cart before retrying…')));
    xhr.addEventListener('timeout', () => finish(new Error('The upload took too long. Checking your cart before retrying…')));
    try {
      xhr.open('POST', target.href);
      xhr.setRequestHeader('Accept', 'application/json');
      // Allow large uploads on slower connections, while bounding a stalled request.
      xhr.timeout = Math.min(600000, Math.max(90000, Math.ceil(bytes / 32768 * 1000) + 60000));
      render();
      xhr.send(body);
    } catch (error) { finish(error); }
  });
  const readCart = async url => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Your cart could not be checked. Please try again.');
      return await response.json();
    } finally { clearTimeout(timer); }
  };
  const authStorageKey = '3dh-auth-return-v1';
  const loginGate = (cartUrl, { verify } = {}) => {
    const token = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const returnTo = new URL(cartUrl, location.href);
    returnTo.searchParams.delete('heirloom_auth_return');
    const loginUrl = new URL('/customer_authentication/login', location.origin);
    loginUrl.searchParams.set('return_to', `${returnTo.pathname}${returnTo.search}`);
    const mobile = window.matchMedia?.('(max-width: 760px)').matches && (window.matchMedia?.('(pointer: coarse)').matches || /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent || ''));
    returnTo.searchParams.set('heirloom_auth_return', token);
    const popupUrl = new URL(loginUrl);
    popupUrl.searchParams.set('return_to', `${returnTo.pathname}${returnTo.search}`);
    const popup = window.open(popupUrl.href, `3dheirloom-customer-login-${token}`, mobile ? '' : 'popup,width=460,height=720,resizable,scrollbars=yes');
    if (!popup) return { loginUrl: loginUrl.href, blocked: true };
    let settled = false;
    let timer = null;
    let deadline = null;
    let checking = false;
    let resolveWait;
    const finish = (authenticated) => {
      if (settled) return;
      settled = true;
      if (timer) clearInterval(timer);
      if (deadline) clearTimeout(deadline);
      window.removeEventListener('message', onMessage);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('pagehide', cancel);
      window.removeEventListener('focus', onFocus);
      try { popup.close(); } catch { /* Shopify may have isolated the popup */ }
      resolveWait(authenticated);
    };
    const cancel = () => finish(false);
    const hasToken = () => {
      try { return localStorage.getItem(authStorageKey) === token; } catch { return false; }
    };
    const onMessage = event => {
      if (event.origin === location.origin && event.data?.type === '3dh-auth-complete' && event.data.token === token) finish(true);
    };
    const onStorage = event => {
      if (event.key === authStorageKey && event.newValue === token) finish(true);
    };
    const onFocus = async () => {
      if (settled || checking || !verify) return;
      checking = true;
      try { if (await verify() === true) finish(true); }
      catch { /* Failed verification leaves sign-in available until cancellation or deadline. */ }
      finally { checking = false; }
    };
    const wait = new Promise(resolve => {
      resolveWait = resolve;
      window.addEventListener('message', onMessage);
      window.addEventListener('storage', onStorage);
      window.addEventListener('pagehide', cancel, { once: true });
      window.addEventListener('focus', onFocus);
      deadline = setTimeout(cancel, 120000);
      timer = setInterval(() => {
        if (hasToken()) finish(true);
        // Shopify isolates its account window: popup.closed can be true while it is still open.
      }, 250);
    });
    return { loginUrl: loginUrl.href, popup, wait, close: cancel };
  };
  globalThis.HeirloomCartUpload = { send, readCart, beginAttempt, loginGate, takeCartSection, isCartSectionCurrent, invalidateCartSection };
})();
